import { Modal } from "@/components/v2";
import { Button, Card } from "@rumbledirect/ui";
import { Col, Flex, Row, Space } from "antd";
import { useState } from "react";
import { getConfigValue } from "@rumble-it/common-lib/src/config";
import { useAffiliate } from "@/lib/hooks/useAffiliate";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser";

import { DossierSummary } from "@/pages/admin/dossiers/components/dossier-summary";
import { DriverCard } from "@/pages/admin/dossiers/components/driver-card";
import { DossierCard } from "@/pages/admin/dossiers/components/dossier-card";
import { ServicePartnersCard } from "@/pages/admin/dossiers/components/servicepartner-card";
import { MediaCard } from "@/pages/admin/dossiers/components/media-card";
import { VehicleHandInLocationCard } from "@/pages/admin/dossiers/components/vehicle-handin-location-card";
import { InsuranceCompanyCard } from "@/pages/admin/dossiers/components/insurance-company-card";
import { DealershipCompanyCard } from "@/pages/admin/dossiers/components/dealership-company-card";
import { AppointmentsCard } from "@/pages/admin/dossiers/components/appointments-card";
import { Dossier } from "@rumble-it/common-lib/types/models/Dossier.interface";
import { Claim } from "@rumble-it/common-lib/types/models/Claim.interface";
import { ClaimDossierOverviewActionsCard } from "../components/ClaimDossierOverviewActionsCard";
import { ClaimDossierInsuranceInfoCard } from "../components/ClaimDossierInsuranceInfoCard";
import { ClaimDossierOverviewTimelineCard } from "../components/ClaimDossierOverviewTimelineCard";
import { TaskCreate } from "@/pages/admin/tasks/TaskCreate";
// import { ClaimDossierRepairTimelineCard } from "../components/ClaimDossierRepairTimelineCard";
import { ClaimDossierRecommendationCard } from "../components/ClaimDossierRecommendationCard";
import { ClaimDossierRecommendationModal } from "../components/ClaimDossierRecommendationModal";
import { AgentDossierActionsCard } from "../../agents/components/AgentDossierActionsCard";
import { ClaimDossierRepairTimelineCard } from "../components/ClaimDossierRepairTimelineCard";

export const ClaimDossierOverviewTab = ({
  data,
  claim,
  isLoading,
  setActiveTab,
}: {
  data: Dossier | undefined;
  claim: Claim | undefined | null;
  isLoading: boolean;
  setActiveTab: (tabKey: string) => void;
}) => {
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isRecommendationModalOpen, setIsRecommendationModalOpen] =
    useState(false);

  const { currentAffiliate } = useAffiliate();
  const currentUser = useCurrentUser();
  const hasPortalLinkPermission = (currentUser?.permissions || []).includes(
    "admin_dossier_link_portal",
  );
  const PORTAL_URL = getConfigValue("portalUrl");
  const canLinkToPortalCalculationUpload =
    hasPortalLinkPermission && !!PORTAL_URL && !!currentAffiliate && !!data?.id;
  const calculationUploadUrl = `${PORTAL_URL}/calculation-upload?dossierId=${data?.id}&affiliateId=${currentAffiliate}`;

  const hasRequestSent = data?.dossierUsageEmailSentDriver.some(
    (email) =>
      email.emailSent?.subjectWithoutStatementsAndExpressions ===
      "Verzoek foto\u0027s aanleveren",
  );
  return (
    <Flex gap={20} className="w-full !px-0">
      <Col flex="auto" className="!px-0">
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <DossierSummary dossier={data} isLoading={isLoading} />
          </Col>

          <Col xxl={{ span: 12 }} span={24}>
            <Flex vertical gap={16}>
              <DossierCard dossier={data} isLoading={isLoading} />
              <MediaCard dossier={data} isLoading={isLoading} />
              <ClaimDossierInsuranceInfoCard isLoading={isLoading} />
            </Flex>
          </Col>

          <Col xxl={{ span: 12 }} span={24}>
            <Flex vertical gap={16}>
              {/* <DossierTimelineCard dossier={data} isLoading={isLoading} /> */}
              {claim &&
                claim.status !== "concept" &&
                claim.status !== "claim_verstuurd" && (
                  <ClaimDossierRecommendationCard
                    dossier={data}
                    isLoading={isLoading}
                    onClickDetails={() => setIsRecommendationModalOpen(true)}
                  />
                )}
              <DriverCard driver={data?.driver} isLoading={isLoading} />
              <AppointmentsCard dossier={data} isLoading={isLoading} />
              <ServicePartnersCard
                servicePartners={data?.servicePartners}
                isLoading={isLoading}
              />
              <InsuranceCompanyCard
                insuranceCompany={data?.insuranceCompany}
                isLoading={isLoading}
              />
              <DealershipCompanyCard
                dealershipCompany={data?.dealershipCompany}
                isLoading={isLoading}
              />
              <VehicleHandInLocationCard dossier={data} isLoading={isLoading} />
            </Flex>
          </Col>
        </Row>
      </Col>
      <Col className="!px-0 2xl:min-w-[400px] min-w-[300px]">
        <ClaimDossierOverviewActionsCard
          claim={claim}
          isLoading={isLoading}
          setActiveTab={setActiveTab}
        />

        {!hasRequestSent && (
          <AgentDossierActionsCard dossier={data} isLoading={isLoading} />
        )}

        <ClaimDossierOverviewTimelineCard claim={claim} isLoading={isLoading} />
        <ClaimDossierRepairTimelineCard isLoading={isLoading} />

        <Card className="w-full mt-5" title={"Losse acties"}>
          <Space direction="vertical" className="w-full">
            <Button size="large" className="w-full">
              Voeg notitie toe
            </Button>
            <Button
              size="large"
              className="w-full"
              onClick={() => setIsTaskModalOpen(true)}
            >
              Voeg taak toe
            </Button>
            {canLinkToPortalCalculationUpload && (
              <a
                className="w-full"
                target="_blank"
                href={calculationUploadUrl}
                rel="noreferrer"
              >
                <Button size="large" className="w-full">
                  Portaal: Calculatie uploaden
                </Button>
              </a>
            )}
          </Space>
        </Card>
      </Col>
      <Modal
        open={isTaskModalOpen}
        onCancel={() => setIsTaskModalOpen(false)}
        width={800}
      >
        <TaskCreate
          onSuccess={() => setIsTaskModalOpen(false)}
          dossier={data}
        />
      </Modal>
      <ClaimDossierRecommendationModal
        open={isRecommendationModalOpen}
        onClose={() => setIsRecommendationModalOpen(false)}
        dossier={data}
      />
    </Flex>
  );
};
